import type { BoggleResolvedChallengeCore } from './config/challenges/coreTypes'
import type {
  BoggleResolvedChallengeBoardDistribution,
  BoggleResolvedChallengeBonus,
  BoggleResolvedChallengeConfig,
} from './config/challenges/modTypes'
import type {
  BogglePlaygroundSettings,
  BogglePlaygroundSettingsInput,
} from './config/playground'
import type { BoggleChallengeSpecialTileRevealEntry } from './config/specialTiles/challengeSpecialTileTypes'
import { pickBoggleChallengeCatalogFromRollSeed } from './config/challenges/catalogPick'
import type { BoggleChallengeConfigurationId } from './config/challenges/registry'
import type { BoggleChallengePathMode } from './config/challengePathMode'
import {
  getBoggleChallengeManualOptions,
  resolveBoggleManualChallengeEntries,
  type BoggleChallengeManualOptions,
  type BoggleChallengeManualSelection,
} from './lib/challenge/manualOptions'
import { buildBoggleResolvedChallengeGameplay } from './lib/challenge/resolvedModOps/resolveGameplay'
import {
  applyResolvedChallengeEffects,
  buildResolvedChallengeCores,
  resolveBoggleChallengeConfigurationSeedState,
  getBoggleChallengePracticeSeed,
  resolveBoggleChallengeConfiguredSeedState,
  toBoggleResolvedChallengeRun,
  type BoggleResolvedChallengeRun,
} from './lib/challenge/utils'
import { normalizeBogglePlaygroundSettings } from './lib/playground'
import { getBoggleGameModeDefinition, type BoggleGameModeId } from './gameModes'

export type {
  BoggleChallengeConfigurationScoreInput,
  BoggleChallengeConfigurationStepContext,
  BoggleResolvedChallengeConfigurationSeedState,
} from './config/challenges/types'
export type { BoggleChallengeVowelRatioBand } from './config/challenges/modTypes'
export type {
  BoggleChallengeConfigurationId,
} from './config/challenges/registry'
export {
  getBoggleChallengeConfigurationDefinition,
  isBoggleChallengeConfigurationId,
} from './config/challenges/registry'
export { pickBoggleChallengeCatalogFromRollSeed } from './config/challenges/catalogPick'
export type {
  BoggleChallengeRolledCoreState,
  BoggleResolvedChallengeCore,
} from './config/challenges/coreTypes'
export type { BoggleResolvedChallengeRun } from './lib/challenge/utils'
export type {
  BoggleChallengeManualOptions,
  BoggleChallengeManualSelection,
} from './lib/challenge/manualOptions'
export {
  getBoggleChallengeManualOptions,
  getBoggleChallengePracticeSeed,
}

export type BoggleResolvedChallengePracticeRun = BoggleResolvedChallengeRun & {
  practiceSeed: number
}

export type BogglePlaygroundBonusPreview = {
  bonuses: readonly BoggleResolvedChallengeBonus[]
  boardDistribution: BoggleResolvedChallengeBoardDistribution | null
  cores: readonly BoggleResolvedChallengeCore[]
  gameplay: BoggleResolvedChallengeConfig['gameplay']
  pathMode: BoggleChallengePathMode
  settings: BogglePlaygroundSettings
  specialTileReveals: readonly BoggleChallengeSpecialTileRevealEntry[]
}

export function resolveBoggleChallengeConfiguredRun(
  gameModeId: BoggleGameModeId,
  seed: number | string,
) {
  const resolved = resolveBoggleChallengeConfiguredSeedState(gameModeId, seed)
  if (resolved === null) {
    return null
  }

  return toBoggleResolvedChallengeRun(gameModeId, resolved)
}

export function resolveBoggleChallengeRun(seed: number | string): BoggleResolvedChallengeRun {
  const { configurationId, gameModeId } = pickBoggleChallengeCatalogFromRollSeed(seed)
  return resolveBoggleChallengeConfigurationRun(gameModeId, configurationId, seed)
}

export function resolveBoggleChallengeConfigurationRun(
  gameModeId: BoggleGameModeId,
  configurationId: BoggleChallengeConfigurationId,
  seed: number | string,
): BoggleResolvedChallengeRun {
  const resolved = resolveBoggleChallengeConfigurationSeedState(
    gameModeId,
    configurationId,
    seed,
  )

  if (resolved === null) {
    throw new Error(`Challenge configuration ${configurationId} is not available for ${gameModeId}`)
  }

  return toBoggleResolvedChallengeRun(gameModeId, resolved)
}

export function resolveBogglePlaygroundBonusPreview(
  gameModeId: BoggleGameModeId,
  seed: number | string,
  selection: BoggleChallengeManualSelection,
  baseSettings?: BogglePlaygroundSettings,
): BogglePlaygroundBonusPreview {
  const gameMode = getBoggleGameModeDefinition(gameModeId)
  const resolved = resolveBoggleManualChallengeEntries(gameModeId, selection, seed)
  const settings = applyResolvedChallengeEffects(
    baseSettings ?? gameMode.corePreset,
    resolved,
  )
  const challenge = {
    bonuses: resolved.bonuses,
    boardDistribution: resolved.boardDistribution,
  }

  return {
    bonuses: resolved.bonuses,
    boardDistribution: resolved.boardDistribution,
    cores: buildResolvedChallengeCores(gameModeId, settings),
    gameplay: buildBoggleResolvedChallengeGameplay(challenge),
    pathMode: 'free',
    settings,
    specialTileReveals: [],
  }
}

export function resolveBoggleChallengePracticeRun(
  seed: number | string,
): BoggleResolvedChallengePracticeRun {
  const { configurationId, gameModeId } = pickBoggleChallengeCatalogFromRollSeed(seed)
  return resolveBoggleChallengeConfigurationPracticeRun(gameModeId, configurationId, seed)
}

export function resolveBoggleChallengeConfigurationPracticeRun(
  gameModeId: BoggleGameModeId,
  configurationId: BoggleChallengeConfigurationId,
  seed: number | string,
): BoggleResolvedChallengePracticeRun {
  const resolved = resolveBoggleChallengeConfigurationSeedState(
    gameModeId,
    configurationId,
    seed,
  )

  if (resolved === null) {
    throw new Error(`Challenge configuration ${configurationId} is not available for ${gameModeId}`)
  }

  return toBoggleResolvedChallengePracticeRun(gameModeId, seed, resolved)
}

function toBoggleResolvedChallengePracticeRun(
  gameModeId: BoggleGameModeId,
  seed: number | string,
  resolved: NonNullable<ReturnType<typeof resolveBoggleChallengeConfigurationSeedState>>,
): BoggleResolvedChallengePracticeRun {
  const gameMode = getBoggleGameModeDefinition(gameModeId)
  const challengeSettings = resolved.rolledCoreState.settings

  let practicePatches: BogglePlaygroundSettingsInput = {
    ...gameMode.challenge.practiceOverrides,
    gridSize: challengeSettings.gridSize,
  }

  if (gameModeId === 'sprint') {
    practicePatches = {
      ...practicePatches,
      intervalTimeSeconds: challengeSettings.intervalTimeSeconds,
    }
  }

  const settings = applyResolvedChallengeEffects(
    normalizeBogglePlaygroundSettings(practicePatches, resolved.rolledCoreState.settings),
    resolved,
  )

  return {
    aceScore: resolved.scoreThresholds.aceScore,
    bonuses: resolved.bonuses,
    boardDistribution: resolved.boardDistribution,
    configurationId: resolved.configurationId,
    cores: buildResolvedChallengeCores(gameModeId, settings),
    gameplay: buildBoggleResolvedChallengeGameplay({
      bonuses: resolved.bonuses,
      boardDistribution: resolved.boardDistribution,
      vowelRatioBand: resolved.vowelRatioBand ?? null,
    }),
    pathMode: resolved.pathMode,
    practiceSeed: getBoggleChallengePracticeSeed(gameModeId, seed),
    specialTileReveals: resolved.rolledCoreState.specialTileReveals,
    settings,
    targetScore: resolved.scoreThresholds.targetScore,
  }
}
